import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { useStore } from "vuex";
import useDate from '@/services/utils/day';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

const pdfPrinter = () => {
    const { formatDate } = useDate();
    const store = useStore();

    const userName = store.state.user.user.name;

    const exportToPDF = (company, plans, date, payload) => {
        const doc = {
            pageSize: 'A4',
            pageMargins: [30, 100, 30, 80],
            pageOrientation: 'portrait',
            header: getHeader(company),
            footer: getFooter(userName),
            content: getContent(plans, date, payload),
            styles: {
                header: {
                    fontSize: 16
                }
            },
            defaultStyle: {
                color: 'black',
                fontSize: 7,
                font: 'SulaimanLipi'
            },
            info: {
                title: 'PRODUCTION PLAN'
            }
        }

        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    }

    const getContent = (plans, date, payload) => {
        return [
            {
                text: 'PRODUCTION PLAN',
                style: 'header',
                alignment: 'center',
                margin: [0, 0, 20, 10],
                bold: true,
                color: 'black',
                decoration: 'underline'
            },
            {
                stack: getFilterLines(date, payload)
            },
            {
                margin: [0, 10, 0, 0],
                table: {
                    headerRows: 1,
                    widths: getColumnWidths(),
                    body: getTableBody(plans)
                },
                layout: {
                    fillColor: function (rowIndex) {
                        if (rowIndex === 0) return '#f3f2f7';
                        return null;
                    },
                    hLineWidth: function () {
                        return 0.5;
                    },
                    vLineWidth: function () {
                        return 0.5;
                    },
                    paddingLeft: function () {
                        return CELL_PADDING;
                    },
                    paddingRight: function () {
                        return CELL_PADDING;
                    },
                    paddingTop: function () {
                        return CELL_PADDING;
                    },
                    paddingBottom: function () {
                        return CELL_PADDING;
                    }
                }
            }
        ]
    }

    const PAGE_CONTENT_WIDTH = 535;

    const CELL_PADDING = 2;

    const COLUMN_PERCENTS = [8, 11, 11, 10, 14, 5, 5, 6, 9, 12, 9];

    const getColumnWidths = () => {
        const paddingTotal = COLUMN_PERCENTS.length * (CELL_PADDING * 2 + 0.5);
        const usableWidth = PAGE_CONTENT_WIDTH - paddingTotal;

        return COLUMN_PERCENTS.map(function (percent) {
            return (usableWidth * percent) / 100;
        });
    }

    const getFilterLines = (date, payload) => {
        const lines = [{ text: `Date: ${date.startDate} - ${date.endDate}` }];

        if (payload.status) lines.push({ text: 'Status: ' + payload.status });
        if (payload.productName) lines.push({ text: 'Product: ' + payload.productName });
        if (payload.machineName) lines.push({ text: 'Machine/Line: ' + payload.machineName });
        if (payload.searchTerm) lines.push({ text: 'Search: ' + payload.searchTerm });

        return lines;
    }

    const getTableBody = (plans) => {
        const header = getTableHead();
        const rows = getTableRow(plans);
        return [
            header,
            ...rows
        ]
    }

    const getTableHead = () => {
        return [
            { text: 'PLAN NO', bold: true },
            { text: 'TITLE', bold: true },
            { text: 'PRODUCT', bold: true },
            { text: 'MACHINE/LINE', bold: true },
            { text: 'SCHEDULE', bold: true },
            { text: 'PLAN QTY', alignment: 'right', bold: true },
            { text: 'PROD QTY', alignment: 'right', bold: true },
            { text: 'REMAIN', alignment: 'right', bold: true },
            { text: 'ORDER', bold: true },
            { text: 'CUSTOMER', bold: true },
            { text: 'STATUS', bold: true }
        ]
    }

    const getTableRow = (plans) => {
        const rows = plans.map(function (plan) {
            return [
                { text: displayValue(plan.plan_number) },
                { text: rowTitle(plan) },
                { text: displayValue(plan.product_name) },
                { text: displayValue(plan.machine_name) },
                { text: displayValue(plan.schedule_text) },
                { text: displayQty(plan.qty), alignment: 'right' },
                { text: displayQty(plan.produced_qty), alignment: 'right' },
                { text: remainingText(plan), alignment: 'right' },
                { text: displayValue(plan.order_number) },
                { text: displayValue(plan.customer_name) },
                { text: statusLabel(plan.status) }
            ];
        });

        rows.push(getTotalRow(plans));
        return rows;
    }

    const getTotalRow = (plans) => {
        return [
            { text: 'Total', colSpan: 5, alignment: 'right', bold: true },
            {}, {}, {}, {},
            { text: displayQty(sumBy(plans, 'qty')), alignment: 'right', bold: true },
            { text: displayQty(sumBy(plans, 'produced_qty')), alignment: 'right', bold: true },
            { text: displayQty(sumBy(plans, 'remaining_qty')), alignment: 'right', bold: true },
            {}, {}, {}
        ]
    }

    const sumBy = (plans, key) => {
        return plans.reduce(function (sum, plan) {
            return sum + toNumber(plan[key]);
        }, 0);
    }

    const toNumber = (value) => {
        const parsed = parseFloat(value);
        if (isNaN(parsed)) return 0;
        return parsed;
    }

    const displayQty = (value) => {
        return toNumber(value).toLocaleString();
    }

    const displayValue = (value) => {
        if (!value) return '-';
        return value;
    }

    const rowTitle = (plan) => {
        if (plan.title) return plan.title;
        return displayValue(plan.product_name);
    }

    const remainingText = (plan) => {
        const remaining = displayQty(plan.remaining_qty);
        if (toNumber(plan.over_produced_qty) > 0) return `${remaining} (+${displayQty(plan.over_produced_qty)})`;
        return remaining;
    }

    const statusLabel = (status) => {
        if (!status) return '-';
        return status.replace('_', ' ');
    }

    const getHeader = (company) => {
        return {
            margin: [30, 25, 30, 10],
            columns: [
                {
                    alignment: 'left',
                    image: company.logo64,
                    maxHeight: 60
                },
                {
                    alignment: 'right',
                    stack: [
                        company.name,
                        'Address: ' + company.address,
                        'Phone: ' + company.phone,
                        'E-mail: ' + company.email
                    ]
                }
            ]
        }
    }

    const getFooter = (userName) => {
        return {
            margin: [25, 0],
            columns: [
                {
                    columns: [
                        {
                            width: '90%',
                            alignment: 'left',
                            text: 'N.B: This is a system generated documents and requires no manual signature.',
                            margin: [5, 53, 0, 0],
                            fontSize: 8
                        }
                    ]
                },
                {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        {
                            text: 'Printed by',
                            bold: true
                        },
                        {
                            text: userName
                        },
                        {
                            text: formatDate(new Date())
                        }
                    ]
                }
            ]
        }
    }

    return {
        exportToPDF
    }
}

export default pdfPrinter;
